import { Box, Button, Icon, SxProps } from "@outschool/backpack";
import * as icons from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { onlineClassesPath } from "@outschool/routes";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  Carousel,
  IconButton,
  MobileDrawer,
} from "@outschool/ui-legacy-component-library";
import { useIsBot, useIsMounted, useLinkComponent } from "@outschool/ui-utils";
import React, { useCallback, useState } from "react";

import { CategoryPartial } from "./navbarCategories";

export default function MobileCategoryNavbar({
  categories,
  sx = {},
  onClickLink,
}: {
  categories: CategoryPartial[];
  onClickLink?: () => void;
  sx?: SxProps;
}) {
  const isMounted = useIsMounted();
  const { t } = useTranslation(
    "ui-components-website\\CategoryNavbar\\MobileCategoryNavbar"
  );
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const closeDrawer = useCallback(() => setDrawerOpen(false), [setDrawerOpen]);
  const CategoryDrawerMemo = React.useMemo(() => {
    return (
      <CategoryDrawer
        open={drawerOpen}
        categories={categories}
        onClose={closeDrawer}
      />
    );
  }, [categories, closeDrawer, drawerOpen]);
  const isBot = useIsBot();

  if (!isMounted) {
    return <></>;
  }

  return (
    <Box
      sx={[
        {
          display: "flex",
          height: "56px",
          overflowX: "hidden",
          overflowY: "hidden",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <TrackedButton
        variant="link"
        sx={{
          fontSize: "14px",
          padding: 0,
          paddingRight: "12px",
          fontWeight: "fontWeightRegular",
          paddingLeft: "20px",
          boxShadow: "0px 2px 20px -10px rgba(0,0,0,0.25)",
        }}
        onClick={() => setDrawerOpen(true)}
        trackingName="category-navbar-mobile-browse"
        trackViews={true}
      >
        <Icon icon={icons.faBook} />
        &nbsp;{t("Browse")} <Icon icon={icons.faChevronDown} />
      </TrackedButton>
      <Box
        sx={{
          flexGrow: 1,
          flexBasis: 0,
          width: "0px",
          marginLeft: "4px",
        }}
      >
        <Carousel>
          {categories
            .filter(c => c.can_link)
            .map((category, index) => (
              <React.Fragment key={index}>
                <CarouselLink
                  category={category}
                  sharedProperties={{ index }}
                  onClickLink={onClickLink ? onClickLink : undefined}
                />
                {/* for prerender only, expand all subcategories into the carousel */}
                {isBot &&
                  category.subcategories?.map(subcategory => (
                    <React.Fragment key={subcategory.slug}>
                      <CarouselLink category={subcategory} />
                      {subcategory.subcategories?.map(nestedSubcategory => (
                        <CarouselLink category={nestedSubcategory} />
                      ))}
                    </React.Fragment>
                  ))}
              </React.Fragment>
            ))}
        </Carousel>
      </Box>
      {CategoryDrawerMemo}
    </Box>
  );
}

function CarouselLink({
  category,
  sharedProperties,
  onClickLink,
}: {
  category: CategoryPartial;
  sharedProperties?: { [key: string]: number | string };
  onClickLink?: () => void;
}) {
  const Link = useLinkComponent();

  return (
    <Link
      key={category.slug}
      to={
        category.path
          ? category.path
          : onlineClassesPath(category.slug, category.basePath)
      }
      trackingName="category-navbar-mobile-pill"
      trackingUniqueId={category.slug}
      trackViews={true}
      sharedProperties={sharedProperties}
      onClick={onClickLink ? onClickLink : undefined}
    >
      <Button
        component="div"
        size="small"
        sx={{
          height: "40px",
          lineHeight: "unset",
          backgroundColor: "grey.100",
          borderStyle: "none",
          fontSize: "16px",
          color: "grey.900",
          fontWeight: "fontWeightRegular",
          paddingX: "16px",
          paddingY: "8px",
          marginX: "4px",
          marginY: "8px",
        }}
      >
        {category.nameTitleCased}
      </Button>
    </Link>
  );
}

function CategoryDrawer({
  open,
  categories,
  onClose,
}: {
  open: boolean;
  categories: CategoryPartial[];
  onClose: () => void;
}) {
  const { t } = useTranslation(
    "ui-components-website\\CategoryNavbar\\MobileCategoryNavbar"
  );
  const [selectedCategory, setSelectedCategory] = useState<CategoryPartial>();

  const closeAll = useCallback(() => {
    setSelectedCategory(undefined);
    onClose();
  }, [onClose, setSelectedCategory]);

  return (
    <MobileDrawer
      sx={{ width: "295px", minWidth: "295px" }}
      open={open}
      onClose={closeAll}
    >
      <Box
        sx={{
          padding: "24px",
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            fontSize: "16px",
            lineHeight: "24px",
            fontWeight: "fontWeightBold",
            marginBottom: "16px",
            color: "primary.900",
          }}
        >
          {t("Browse categories")}
        </Box>
        {categories.map(category => (
          <CategoryButton
            key={category.slug}
            category={category}
            onClick={() => setSelectedCategory(category)}
          />
        ))}
      </Box>
      {open && (
        <SubcategoryDrawerContents
          category={selectedCategory}
          onClose={closeAll}
          onBack={() => setSelectedCategory(undefined)}
        />
      )}
    </MobileDrawer>
  );
}

function CategoryButton({
  category,
  onClick,
}: {
  category: CategoryPartial;
  onClick: () => void;
}) {
  return (
    <Box>
      <TrackedButton
        variant="link"
        sx={{
          fontSize: "16px",
          lineHeight: "24px",
          fontWeight: "fontWeightRegular",
          color: "primary.900",
          marginBottom: "16px",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
        trackingName="category-navbar-mobile-drawer-category"
        trackingUniqueId={category.slug}
        trackViews={true}
        onClick={onClick}
      >
        <Box
          sx={{
            flexGrow: 1,
            textAlign: "start",
            overflowX: "hidden",
            whiteSpace: "normal",
          }}
        >
          {category.nameTitleCased}
        </Box>
        <Icon
          sx={{
            fontSize: "1em",
            color: "primary.500",
          }}
          icon={icons.faChevronRight}
        />
      </TrackedButton>
    </Box>
  );
}

function SubcategoryDrawerContents({
  category,
  onBack,
  onClose,
}: {
  category?: CategoryPartial;
  onBack: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(
    "ui-components-website\\CategoryNavbar\\MobileCategoryNavbar"
  );

  const renderHeaderCallback = React.useCallback(() => {
    return (
      <IconButton
        aria-label={t("Back")}
        icon={icons.faChevronLeft}
        iconProps={{ sx: { fontSize: "1.333em" } }}
        onClick={onBack}
        trackingName="category-navbar-mobile-drawer-back"
      />
    );
  }, [onBack, t]);
  return (
    <MobileDrawer
      open={!!category}
      onClose={onClose}
      renderHeader={renderHeaderCallback}
      zIndex={3000}
      sx={{ width: "295px", minWidth: "295px" }}
      portalSx={{ opacity: 0.01 }}
    >
      {category && (
        <Box
          sx={{
            padding: "24px",
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              marginBottom: "16px",
            }}
          >
            <Box
              sx={{
                fontSize: "16px",
                lineHeight: "24px",
                fontWeight: "fontWeightBold",
                marginBottom: "4px",
                color: "primary.900",
              }}
            >
              {category.nameTitleCased}
            </Box>
            {category.can_link && (
              <SubcategoryLink
                category={category}
                subcategory={{
                  nameTitleCased: t("View all"),
                  slug: category.slug,
                }}
                sx={{ color: "grey.400" }}
                onClick={onClose}
              />
            )}
          </Box>
          {category.subcategories?.map(subcategory => (
            <React.Fragment key={subcategory.slug}>
              <SubcategoryLink
                category={category}
                subcategory={subcategory}
                sx={{
                  color: "primary.900",
                  fontWeight: "500",
                  fontSize: "1em",
                }}
                onClick={onClose}
              />
              {subcategory.subcategories?.map(nestedSubcategory => (
                <SubcategoryLink
                  key={nestedSubcategory.slug}
                  category={category}
                  subcategory={nestedSubcategory}
                  sx={{ color: "primary.900" }}
                  onClick={onClose}
                />
              ))}
            </React.Fragment>
          ))}
        </Box>
      )}
    </MobileDrawer>
  );
}
function SubcategoryLink({
  subcategory,
  category,
  onClick,
  sx,
}: {
  subcategory: CategoryPartial;
  category: CategoryPartial;
  onClick: () => void;
  sx: SxProps;
}) {
  const Link = useLinkComponent();
  return (
    <Link
      to={
        subcategory.path
          ? subcategory.path
          : onlineClassesPath(subcategory.slug, subcategory.basePath)
      }
      trackingName="category-navbar-mobile-drawer-subcategory"
      trackingUniqueId={`${category.slug}/${subcategory.slug}`}
      trackViews={true}
      onClick={onClick}
    >
      <Box
        sx={[
          {
            marginBottom: "16px",
            fontSize: "16px",
            fontWeight: "fontWeightRegular",
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {subcategory.nameTitleCased}
      </Box>
    </Link>
  );
}
